import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom'
import {
  Button,
  Checkbox,
  InputField,
  ScreenLoader,
} from '@enamelai/design-system'
import { useAppSelector } from '@store/hooks'
import { RootState } from '@store/index'
import type {
  CustomReportRow,
  PresetsReturnState,
} from '@constants/Reports/presetsAutomation.constants'
import {
  PRESETS_CUSTOM_REPORTS_MOCK,
  getPresetsPathWithTab,
} from '@constants/Reports/presetsAutomation.constants'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DEFAULT_FILTERS } from '@constants/Reports/reportsDefaultToolbar.constants'
import { useSelector } from 'react-redux'
import ReportPreviewContainer from './PreviewTemplate/ReportPreviewContainer'
import { useReportMetricsSchema } from '@hooks/reports/useReportMetricsSchema'
import { useCustomReportCategories } from '@hooks/reports/useCustomReportCategories'
import type { ReportsOutletContext } from '@pages/Reports/Reports.page'
import { toSentenceCase } from '@utils/textCase.util'
import ReportsSkeleton from './ReportsSkeleton.page'

const CUSTOM_SKELETON_DURATION_MS = 500

const DEFAULT_SELECTED_CATEGORIES: string[] = ['patients']

export const ALL_METRICS_WITH_DEFAULT_SELECTION: Record<string, boolean> = {
  summary: true,
  lastVisitDistribution: true,
  patientPopulationTrend: true,
  patientAgeDistribution: true,
  highRiskPatients: true,
  rrSummary: false,
  srSummary: false,
  tcfSummary: false,
  saSummary: false,
  saFilledOpen: false,
  saHygieneGaps: false,
  saProductionTargetScheduled: false,
  saNoShows: false,
  cruSummary: false,
  hpSummary: false,
  obtSummary: false,
  ppSummary: false,
  cfeSummary: false,
  cmtSummary: false,
  teSummary: false,
  esSummary: false,
  rraSummary: false,
  rriskSummary: false,
  claimsSummary: false,
  gavSummary: false,
  gavYtd: false,
  gavRunRate: false,
  gavVarianceTrend: false,
  gavProduction: false,
  gavCollection: false,
  gavLocation: false,
  gavProvider: false,
}

function CustomReportBuilderPage(): React.ReactElement {
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )
  const navigate = useNavigate()
  const location = useLocation()
  const previewScrollRef = useRef<HTMLDivElement>(null)
  const [customBuilderRailSlot, setCustomBuilderRailSlot] =
    useState<HTMLElement | null>(null)
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportScheduleReportAction, setReportScheduleReportAction] =
    useState<JSX.Element | null>(null)
  const [reportDownloadPDFAction, setReportDownloadPDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const [payloadData, setPayloadData] = useState<any>(null)
  const [reportPayloadLoading, setReportPayloadLoading] = useState(false)
  const [reportPayloadError, setReportPayloadError] = useState<Error | null>(
    null,
  )
  const hasPayloadData = Object.keys(payloadData ?? {}).length > 0

  useEffect(() => {
    setCustomBuilderRailSlot(
      document.getElementById('reports-preview-pdf-rail'),
    )
  }, [])

  const { id: viewingReportId } = useParams<{ id: string }>()
  const { isPageReady } = useOutletContext<ReportsOutletContext>()
  const isReportsTemplateLoading = useAppSelector(
    (state) =>
      state.reportsTemplate.loading || state.reportsTemplate.data == null,
  )
  const [isCustomSkeleton, setIsCustomSkeleton] = useState(true)
  const [customSkeletonKey, setCustomSkeletonKey] = useState(0)

  useEffect(() => {
    if (!isPageReady) return
    setIsCustomSkeleton(true)
    setCustomSkeletonKey((key) => key + 1)
  }, [viewingReportId, isPageReady])

  useEffect(() => {
    if (!isPageReady || !isCustomSkeleton) return
    const timer = window.setTimeout(() => {
      setIsCustomSkeleton(false)
    }, CUSTOM_SKELETON_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [isCustomSkeleton, customSkeletonKey, isPageReady])
  const customReportFromRedux: CustomReportRow | undefined = useAppSelector(
    (state) =>
      viewingReportId
        ? state.reports.customReports.find((r) => r.id === viewingReportId)
        : undefined,
  )
  const savedReport: CustomReportRow | undefined = useMemo(() => {
    if (!viewingReportId) return undefined
    return (
      customReportFromRedux ??
      PRESETS_CUSTOM_REPORTS_MOCK.find((r) => r.id === viewingReportId)
    )
  }, [viewingReportId, customReportFromRedux])

  const headerBrandEnabled = savedReport?.headerBrandEnabled ?? false
  const [reportTitle, setReportTitle] = useState<string>(
    () => savedReport?.name ?? '',
  )
  const [showTitleError, setShowTitleError] = useState(false)
  const sourceMetricsSchema = useReportMetricsSchema()
  const customReportCategories = useCustomReportCategories()
  const [selectedCategories, setSelectedCategories] = useState<
    Record<string, boolean>
  >({})
  const [selectedMetricIds, setSelectedMetricIds] = useState<
    Record<string, boolean>
  >({})
  if (customReportCategories.length > 0) {
    if (
      Object.keys(selectedCategories).length === 0 &&
      Object.keys(selectedMetricIds).length === 0
    ) {
      setSelectedCategories(
        customReportCategories.reduce(
          (acc, cat) => {
            acc[cat.id] = DEFAULT_SELECTED_CATEGORIES.includes(cat.id)
            return acc
          },
          {} as Record<string, boolean>,
        ),
      )
      setTimeout(() => {
        setSelectedMetricIds(ALL_METRICS_WITH_DEFAULT_SELECTION)
      }, 300)
    }
  }

  const hasSelectedCategory = useMemo(
    () => customReportCategories.some((c) => selectedCategories[c.id]),
    [customReportCategories, selectedCategories],
  )

  /** Metrics available for checking — only those whose category is selected. */
  const availableMetrics = useMemo<any[]>(() => {
    const metrics = sourceMetricsSchema.filter(
      (m) => selectedCategories[m.customCategory as string],
    )
    const metricIds = new Set(metrics.map((m) => m.id))
    const updatedSelectedMetricIds = Object.fromEntries(
      Object.entries(selectedMetricIds).map(([key, value]) => [
        key,
        metricIds.has(key) ? value : false,
      ]),
    )

    setSelectedMetricIds(updatedSelectedMetricIds)

    return metrics
  }, [selectedCategories])

  const toggleCategory = useCallback((categoryId: string) => {
    setSelectedCategories((prev) => {
      const next = { ...prev, [categoryId]: !prev[categoryId] }
      return next
    })
  }, [])

  const toggleMetric = useCallback((metricId: string) => {
    setSelectedMetricIds((prev) => ({ ...prev, [metricId]: !prev[metricId] }))
  }, [])

  const generatedOnDisplay = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  const backToReportHub = (): void => {
    const tab = (location.state as PresetsReturnState | null)?.presetsReturnTab
    if (tab) {
      navigate(getPresetsPathWithTab(tab))
      return
    }
    navigate('/report')
  }

  const trimmedTitle = reportTitle.trim()
  const isTitleValid = trimmedTitle.length > 0

  return !isPageReady || isCustomSkeleton || isReportsTemplateLoading ? (
    <ReportsSkeleton variant="custom" />
  ) : (
    <div className="reports-preview-shell reports-custom-builder-shell">
      <div className="reports-preview-panel">
        <ReportPageHeader>
          {reportSaveViewAction}
          {reportScheduleReportAction}
        </ReportPageHeader>

        <div className="reports-preview-toolbar">
          <ReportFilterToolbar
            filters={REPORTS_DEFAULT_FILTERS}
            onReportSaveViewActionChange={setReportSaveViewAction}
            onReportScheduleReportActionChange={setReportScheduleReportAction}
            onReportDownloadPDFActionChange={setReportDownloadPDFAction}
            pdfDataScope="custom"
            pdfMetrics={selectedMetricIds}
            onScopeAndMetricsChange={(_, metrics) => {
              if (metrics) setSelectedMetricIds(metrics)
            }}
            customCategories={selectedCategories}
            onCustomCategoriesChange={setSelectedCategories}
            customTitle={reportTitle}
            onCustomTitleChange={setReportTitle}
            onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
            onReportPayloadDataChange={(data, loading, error) => {
              setPayloadData(data)
              setReportPayloadLoading(loading)
              setReportPayloadError(error)
            }}
          />
        </div>

        <div className="reports-preview-body reports-custom-builder-body">
          <div
            className="reports-preview-scroll reports-custom-builder-scroll"
            ref={previewScrollRef}
            aria-busy={reportPayloadLoading}
          >
            <ScreenLoader
              open={reportPayloadLoading}
              size="md"
              inline
              aria-label="Loading live report data"
            />
            {reportPayloadError ? (
              <div className="reports-preview-data-state" role="alert">
                <strong>Report data could not be loaded.</strong>
                <span>{reportPayloadError.message}</span>
              </div>
            ) : null}
            {!reportPayloadLoading || hasPayloadData ? (
              <ReportPreviewContainer
                selectedClinic={selectedClinic}
                reportTitle={reportTitle || ''}
                reportFiltersAndMetrics={reportFiltersAndMetrics}
                generatedOnDisplay={generatedOnDisplay}
                headerSwitch={headerBrandEnabled}
                isCustomReport={true}
                payloadData={payloadData}
              />
            ) : null}
          </div>
        </div>
      </div>
      {customBuilderRailSlot
        ? createPortal(
            <aside
              className="reports-preview-aside reports-custom-builder-aside"
              aria-label="Custom report builder"
            >
              <div className="reports-preview-aside__card__title">
                <InputField
                  id="custom-builder-title-input"
                  label="Report title"
                  variant="text"
                  fullWidth
                  className="reports-custom-builder-aside__input"
                  placeholder="Report title e.g. monthly operations"
                  value={reportTitle}
                  onChange={(e) => {
                    setReportTitle(e.target.value)
                    if (showTitleError) setShowTitleError(false)
                  }}
                  error={showTitleError && !isTitleValid}
                  helperText={
                    showTitleError && !isTitleValid
                      ? 'Report title is required.'
                      : undefined
                  }
                  aria-describedby={
                    showTitleError && !isTitleValid
                      ? 'custom-builder-title-error'
                      : undefined
                  }
                  required
                />
              </div>

              <div className="reports-preview-aside__card">
                <p className="reports-preview-aside__card-title">
                  Select category
                </p>
                <ul className="reports-preview-aside__metrics" role="list">
                  {customReportCategories.map((cat) => (
                    <li key={cat.id}>
                      <Checkbox
                        label={toSentenceCase(cat.label)}
                        checked={Boolean(selectedCategories[cat.id])}
                        onChange={() => toggleCategory(cat.id)}
                      />
                    </li>
                  ))}
                </ul>
              </div>

              {hasSelectedCategory ? (
                <div className="reports-preview-aside__card">
                  <p className="reports-preview-aside__card-title">
                    Select metric
                  </p>
                  {availableMetrics.length === 0 ? (
                    <p className="reports-custom-builder-aside__empty">
                      No metrics for the selected categories.
                    </p>
                  ) : (
                    <ul className="reports-preview-aside__metrics" role="list">
                      {availableMetrics.map((metric) => (
                        <li key={metric.id}>
                          <Checkbox
                            label={toSentenceCase(
                              (metric.customLabel || metric.label).replace(
                                ' — ',
                                ' ',
                              ),
                            )}
                            checked={Boolean(selectedMetricIds[metric.id])}
                            onChange={() => toggleMetric(metric.id)}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}

              <div className="reports-preview-aside__actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={backToReportHub}
                >
                  Cancel
                </Button>
                <Button type="button" variant="secondary">
                  Send copy
                </Button>
                {reportDownloadPDFAction}
              </div>
            </aside>,
            customBuilderRailSlot,
          )
        : null}
    </div>
  )
}

export default CustomReportBuilderPage
